import { ReactNode, useContext, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import EventIcon from '@mui/icons-material/Event'
import LayersIcon from '@mui/icons-material/Layers'
import { SPACING } from '@common/theme/spacing'
import { ThemeContext } from '@common/theme'

export interface ViewableSubmission {
  timepointSubmissionId: string
  name: string
  groupName?: string
  isViewable?: boolean
}

export interface ModalityOption {
  name: string
  isViewable?: boolean
}

interface AllTimepointsViewerSelectorProps {
  submissions: ViewableSubmission[]
  modalities: ModalityOption[]
  onOpenViewer: (selectedTimepointSubmissionIds: string[], selectedModalities: string[] | null) => void
  disabled?: boolean
}

const rowSx = {
  py: 0.75,
  px: 2,
  minHeight: 40,
}

const checkboxSx = {
  p: 0.5,
  mr: 1.25,
}

const sectionLabelSx = {
  display: 'block',
  px: 2,
  pt: 1.5,
  pb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: 0.2,
}

function FilterPill({ icon, count, title }: { icon: ReactNode; count: number; title: string }) {
  return (
    <Tooltip title={title}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.375,
          px: 0.75,
          height: 22,
          borderRadius: 11,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {icon}
        {count}
      </Box>
    </Tooltip>
  )
}

export default function AllTimepointsViewerSelector({
  submissions,
  modalities,
  onOpenViewer,
  disabled,
}: AllTimepointsViewerSelectorProps) {
  const [selectedTimepointIds, setSelectedTimepointIds] = useState<string[] | null>(null)
  const [selectedModalities, setSelectedModalities] = useState<string[] | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const open = Boolean(anchorEl)
  // theme.palette.mode is not set by the custom palette, so read the mode from the app's ThemeContext
  const isDarkMode = useContext(ThemeContext)?.themeMode === 'dark'

  const allTimepointIds = submissions.filter((s) => s.isViewable !== false).map((s) => s.timepointSubmissionId)
  const allViewableModalities = modalities.filter((m) => m.isViewable !== false).map((m) => m.name)

  const checkedTimepointIds = selectedTimepointIds ?? []
  const checkedModalities = selectedModalities ?? []
  const hasTimepointSelection = checkedTimepointIds.length > 0
  const hasModalitySelection = checkedModalities.length > 0
  // no explicit selection means no filter, so the viewer opens everything available
  const effectiveTimepoints = hasTimepointSelection ? checkedTimepointIds : allTimepointIds

  const isDisabled = disabled || allTimepointIds.length === 0

  const handleTimepointToggle = (id: string) => {
    setSelectedTimepointIds(
      checkedTimepointIds.includes(id) ? checkedTimepointIds.filter((x) => x !== id) : [...checkedTimepointIds, id],
    )
  }

  const handleModalityToggle = (mod: string) => {
    setSelectedModalities(
      checkedModalities.includes(mod) ? checkedModalities.filter((m) => m !== mod) : [...checkedModalities, mod],
    )
  }

  const allTimepointsSelected =
    allTimepointIds.length > 0 && allTimepointIds.every((id) => checkedTimepointIds.includes(id))
  const someTimepointsSelected = hasTimepointSelection && !allTimepointsSelected

  const allModalitiesSelected =
    allViewableModalities.length > 0 && allViewableModalities.every((m) => checkedModalities.includes(m))
  const someModalitiesSelected = hasModalitySelection && !allModalitiesSelected

  return (
    <Box display="flex" alignItems="flex-start" gap={0.5} flexShrink={0}>
      <Tooltip title="This button opens DICOM data from submitted visits only, non-DICOM datasets are excluded. Filters are disabled when there is one or no submitted visit.">
        <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
      </Tooltip>
      <Badge
        badgeContent={
          <Stack direction="row" spacing={0.5}>
            {hasTimepointSelection && (
              <FilterPill
                icon={<EventIcon sx={{ fontSize: 14 }} />}
                count={checkedTimepointIds.length}
                title={`${checkedTimepointIds.length} of ${allTimepointIds.length} timepoints`}
              />
            )}
            {hasModalitySelection && (
              <FilterPill
                icon={<LayersIcon sx={{ fontSize: 14 }} />}
                count={checkedModalities.length}
                title={`${checkedModalities.length} of ${allViewableModalities.length} modalities`}
              />
            )}
          </Stack>
        }
        invisible={disabled || (!hasTimepointSelection && !hasModalitySelection)}
        sx={{
          flexShrink: 0,
          // keep the pills inside the control's right edge instead of overhanging it
          '& .MuiBadge-badge': {
            p: 0,
            height: 'auto',
            minWidth: 0,
            bgcolor: 'transparent',
            transform: 'translate(0, -50%)',
          },
        }}
      >
        <Box
          ref={containerRef}
          display="flex"
          alignItems="stretch"
          flexShrink={0}
          sx={{
            border: '1px solid',
            borderColor: 'secondary.dark',
            borderRadius: (theme) => theme.spacing(SPACING.borderRadiusSm),
            overflow: 'hidden',
          }}
        >
          <Button
            disabled={isDisabled}
            onClick={() => onOpenViewer(effectiveTimepoints, hasModalitySelection ? checkedModalities : null)}
            sx={{
              color: 'text.primary',
              border: 'none',
              py: SPACING.spacingMd,
              px: SPACING.spacingLg,
              borderRadius: 0,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            Open in viewer
          </Button>

          <Divider orientation="vertical" flexItem sx={{ borderColor: 'secondary.dark', my: 1 }} />
          <Tooltip title="Filter timepoints and modalities">
            <span>
              <IconButton
                size="small"
                disabled={disabled || allTimepointIds.length <= 1}
                onClick={(e) => setAnchorEl(open ? null : e.currentTarget)}
                sx={{ borderRadius: 0, px: SPACING.spacingSm }}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  backgroundColor: (theme) =>
                    isDarkMode ? theme.palette.secondary.main : theme.palette.background.default,
                  border: (theme) => (isDarkMode ? `1px solid ${alpha(theme.palette.secondary.dark, 0.5)}` : 'none'),
                  boxShadow: (theme) => (isDarkMode ? '0px 4px 12px 0px rgba(0, 0, 0, 0.3)' : theme.shadows[4]),
                },
              },
            }}
          >
            <Box display="flex" alignItems="stretch">
              <Box sx={{ minWidth: 260, maxHeight: 460, overflowY: 'auto', pb: 1 }}>
                <Typography sx={sectionLabelSx}>Timepoints</Typography>
                <MenuItem
                  onClick={() => setSelectedTimepointIds(allTimepointsSelected ? [] : [...allTimepointIds])}
                  sx={rowSx}
                >
                  <Checkbox
                    checked={allTimepointsSelected}
                    indeterminate={someTimepointsSelected}
                    disableRipple
                    sx={checkboxSx}
                  />
                  <ListItemText>Select all</ListItemText>
                </MenuItem>
                {submissions.map((s) => {
                  const isViewable = s.isViewable !== false
                  return (
                    <MenuItem
                      key={s.timepointSubmissionId}
                      onClick={() => handleTimepointToggle(s.timepointSubmissionId)}
                      disabled={!isViewable}
                      sx={rowSx}
                    >
                      <Checkbox
                        checked={isViewable && checkedTimepointIds.includes(s.timepointSubmissionId)}
                        disabled={!isViewable}
                        disableRipple
                        sx={checkboxSx}
                      />
                      <ListItemText primary={s.name} />
                    </MenuItem>
                  )
                })}
              </Box>

              <Divider orientation="vertical" flexItem />

              <Box sx={{ minWidth: 200, maxHeight: 460, overflowY: 'auto', pb: 1 }}>
                <Typography sx={sectionLabelSx}>Modalities</Typography>
                {modalities.length === 0 ? (
                  <MenuItem disabled sx={rowSx}>
                    <ListItemText secondary="Loading modalities…" />
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem
                      onClick={() => setSelectedModalities(allModalitiesSelected ? [] : [...allViewableModalities])}
                      sx={rowSx}
                    >
                      <Checkbox
                        checked={allModalitiesSelected}
                        indeterminate={someModalitiesSelected}
                        disableRipple
                        sx={checkboxSx}
                      />
                      <ListItemText>Select all</ListItemText>
                    </MenuItem>
                    {modalities.map((mod) => {
                      const isViewable = mod.isViewable !== false
                      return (
                        <MenuItem
                          key={mod.name}
                          onClick={() => handleModalityToggle(mod.name)}
                          disabled={!isViewable}
                          sx={rowSx}
                        >
                          <Checkbox
                            checked={isViewable && checkedModalities.includes(mod.name)}
                            disabled={!isViewable}
                            disableRipple
                            sx={checkboxSx}
                          />
                          <ListItemText primary={mod.name} />
                        </MenuItem>
                      )
                    })}
                  </>
                )}
              </Box>
            </Box>
          </Popover>
        </Box>
      </Badge>
    </Box>
  )
}
